import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import { CallSplit as BranchIcon } from '@mui/icons-material';
import { getEnhancedDashboard, createScope } from '../../services/releaseManagementApi';

const TEAL = '#1D9E75';

const SCOPE_STATUS_PILL = {
  planning:    { bg: '#FEF3C7', color: '#92400E' },
  in_progress: { bg: '#ECFDF5', color: '#065F46' },
  completed:   { bg: '#ECFDF5', color: '#065F46' },
  merged:      { bg: '#EFF6FF', color: '#1E40AF' },
  blocked:     { bg: '#FEF2F2', color: '#991B1B' },
};

const SCOPE_STATUS_LABELS = {
  planning: 'Planning',
  in_progress: 'Active',
  completed: 'Merged',
  merged: 'Merged',
  blocked: 'Blocked',
};

const SEV_COLORS = {
  critical: '#E24B4A',
  major: '#EF9F27',
  minor: '#378ADD',
  trivial: '#9CA3AF',
};

const DashboardView = ({ releaseId, onNavigateToTree, onNavigateToTab }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newScope, setNewScope] = useState({ name: '', branch_name: '', description: '', scope_type: 'feature' });
  const [creating, setCreating] = useState(false);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getEnhancedDashboard(releaseId);
      setDashboardData(res.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseId]);

  const handleCreateScope = async () => {
    if (!newScope.name.trim()) return;
    try {
      setCreating(true);
      await createScope(releaseId, newScope);
      setCreateDialogOpen(false);
      setNewScope({ name: '', branch_name: '', description: '', scope_type: 'feature' });
      fetchDashboard();
    } catch (err) {
      console.error('Error creating scope:', err);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!dashboardData) {
    return <Alert severity="info">No data available</Alert>;
  }

  // Data derivations
  const scopeSummary = dashboardData.scope_summary || [];

  // Aggregate stories & issues across all scopes for readiness gate
  const totalStories = scopeSummary.reduce((a, s) => a + (s.total_stories || 0), 0);
  const doneStories = scopeSummary.reduce((a, s) => a + (s.done_stories || 0), 0);
  const openStories = totalStories - doneStories;
  const storiesPct = totalStories > 0 ? Math.round((doneStories / totalStories) * 100) : 0;

  const totalIssues = scopeSummary.reduce((a, s) => a + (s.issue_count || 0), 0);
  const resolvedIssues = scopeSummary.reduce((a, s) => a + (s.resolved_issues || 0), 0);
  const openIssues = totalIssues - resolvedIssues;
  const issuesPct = totalIssues > 0 ? Math.round((resolvedIssues / totalIssues) * 100) : 0;

  // Test pass rate
  const testPct = dashboardData.pass_rate ?? 0;
  const hasTests = (dashboardData.total_test_cases || 0) > 0;
  const hasStories = totalStories > 0;
  const hasIssues = totalIssues > 0;

  // Readiness formula: 40/30/30 weighted (tests/stories/issues)
  // Dynamic weight redistribution when a signal has no data
  let readiness = 0;
  const signals = [];
  if (hasTests) signals.push({ pct: testPct, w: 40 });
  if (hasStories) signals.push({ pct: storiesPct, w: 30 });
  if (hasIssues) signals.push({ pct: issuesPct, w: 30 });
  if (signals.length > 0) {
    const totalWeight = signals.reduce((a, s) => a + s.w, 0);
    readiness = Math.round(signals.reduce((a, s) => a + s.pct * (s.w / totalWeight), 0));
  }

  // Critical open issues count
  const criticalOpen = scopeSummary.reduce((a, s) => a + (s.severity_breakdown?.critical || 0), 0);

  // GO requires readiness >= 80% AND zero critical open issues
  const isGo = readiness >= 80 && criticalOpen === 0;

  // Blocking failures
  const blockingCount = dashboardData.failed + (dashboardData.blocked || 0);

  // Bar fill colour helper
  const barColor = (pct) => pct >= 80 ? TEAL : pct >= 50 ? '#F59E0B' : '#E24B4A';

  return (
    <Box>
      {/* ── NO-GO / GO Banner ── */}
      <Box sx={{
        bgcolor: isGo ? '#F0FBF7' : '#FEF2F2',
        border: '1px solid',
        borderColor: isGo ? '#9FE1CB' : '#FECACA',
        borderRadius: '8px', px: '16px', py: '10px', mb: '16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isGo ? TEAL : '#E24B4A' }} />
            <Typography sx={{ fontSize: '1.063rem', fontWeight: 600, color: isGo ? '#085041' : '#991B1B' }}>
              {isGo ? 'Release ready — GO' : 'Release not ready — NO-GO'}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.875rem', color: isGo ? '#085041' : '#991B1B', mt: '2px', ml: '16px' }}>
            {isGo && 'All readiness criteria met'}
            {!isGo && (() => {
              if (readiness >= 80 && criticalOpen > 0) return `Blocked by ${criticalOpen} critical open issue${criticalOpen > 1 ? 's' : ''}`;
              const reasons = [];
              if (criticalOpen > 0) reasons.push(`${criticalOpen} critical issue${criticalOpen > 1 ? 's' : ''} open`);
              if (hasTests && testPct < 95) reasons.push(`Test pass rate ${testPct}%`);
              if (openStories > 0) reasons.push(`${openStories} stories still open`);
              if (openIssues > 0) reasons.push(`${openIssues} issues open`);
              return reasons.join(' · ') || 'Readiness below 80%';
            })()}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: isGo ? TEAL : '#E24B4A' }}>
          {readiness}%
        </Typography>
      </Box>

      {/* ── Stat Cards ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', mb: '16px' }}>
        {/* Total test cases */}
        <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '8px', p: '16px', border: '1px solid #E5E7EB' }}>
          <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 700, mb: '6px' }}>Total test cases</Typography>
          <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: '#111827' }}>{dashboardData.total_test_cases}</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500, mt: '4px' }}>across {scopeSummary.length} scopes</Typography>
        </Box>
        {/* Passed */}
        <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '8px', p: '16px', border: '1px solid #E5E7EB' }}>
          <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 700, mb: '6px' }}>Passed</Typography>
          <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: dashboardData.passed > 0 ? TEAL : '#9CA3AF' }}>{dashboardData.passed}</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500, mt: '4px' }}>of {dashboardData.passed + dashboardData.failed} executed</Typography>
        </Box>
        {/* Failed */}
        <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '8px', p: '16px', border: '1px solid #E5E7EB' }}>
          <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 700, mb: '6px' }}>Failed</Typography>
          <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: dashboardData.failed > 0 ? '#E24B4A' : '#9CA3AF' }}>{dashboardData.failed}</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500, mt: '4px' }}>{blockingCount} blocking</Typography>
        </Box>
        {/* Pass rate */}
        <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '8px', p: '16px', border: '1px solid #E5E7EB' }}>
          <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 700, mb: '6px' }}>Pass rate</Typography>
          <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: dashboardData.pass_rate >= 95 ? TEAL : '#E24B4A' }}>{dashboardData.pass_rate}%</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500, mt: '4px' }}>target &ge; 95%</Typography>
        </Box>
      </Box>

      {/* ── Release Readiness Gate Panel ── */}
      <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', p: '16px', mb: '16px' }}>
        {/* Panel header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: '12px', borderBottom: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ width: 3, height: 20, borderRadius: '2px', bgcolor: TEAL }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Release readiness gate</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.875rem', color: '#9CA3AF' }}>Aggregated across all scopes &middot; 40 / 30 / 30 weighted</Typography>
        </Box>

        {/* 3-column grid: Tests (40%) | Stories (30%) | Issues (30%) */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', mt: '12px' }}>
          {/* Tests column */}
          {(() => {
            const pct = testPct;
            const complete = pct >= 95;
            const valColor = complete ? TEAL : '#E24B4A';
            const failedCount = dashboardData.failed || 0;
            return (
              <Box sx={{ px: '20px', py: '12px', borderRight: '1px solid #D4EDE5', bgcolor: '#F8FAF9', borderRadius: '8px' }}>
                {/* Icon + label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 600,
                    bgcolor: complete ? TEAL : '#E24B4A', color: '#FFFFFF',
                  }}>
                    {complete ? '✓' : '✕'}
                  </Box>
                  <Typography sx={{ fontSize: '0.938rem', fontWeight: 700, color: '#111827' }}>Tests passed</Typography>
                </Box>
                {/* Fraction */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: '2px' }}>
                  <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: valColor }}>{dashboardData.passed}</Typography>
                  <Typography sx={{ fontSize: '1rem', color: '#6B7280', fontWeight: 500 }}>/ {dashboardData.total_test_cases} total</Typography>
                </Box>
                {/* Sub-label */}
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', mb: '10px' }}>
                  {failedCount > 0 ? `${failedCount} failed` : 'All tests passing'}
                </Typography>
                {/* Progress bar */}
                <Box sx={{ height: 8, width: '100%', bgcolor: '#D4EDE5', borderRadius: 9999, overflow: 'hidden', mb: '6px' }}>
                  <Box sx={{ height: '100%', borderRadius: 9999, bgcolor: barColor(pct), width: `${Math.min(pct, 100)}%` }} />
                </Box>
                {/* Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Weight: 40%</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: valColor }}>{pct}% pass rate</Typography>
                </Box>
              </Box>
            );
          })()}

          {/* Stories column */}
          {(() => {
            const pct = storiesPct;
            const complete = pct >= 80 && openStories === 0;
            const valColor = complete ? TEAL : '#E24B4A';
            return (
              <Box sx={{ px: '20px', py: '12px', borderRight: '1px solid #D4EDE5', bgcolor: '#F8FAF9', borderRadius: '8px' }}>
                {/* Icon + label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 600,
                    bgcolor: complete ? TEAL : '#E24B4A', color: '#FFFFFF',
                  }}>
                    {complete ? '✓' : '✕'}
                  </Box>
                  <Typography sx={{ fontSize: '0.938rem', fontWeight: 700, color: '#111827' }}>Stories closed</Typography>
                </Box>
                {/* Fraction */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: '2px' }}>
                  <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: valColor }}>{doneStories}</Typography>
                  <Typography sx={{ fontSize: '1rem', color: '#6B7280', fontWeight: 500 }}>/ {totalStories} total</Typography>
                </Box>
                {/* Sub-label */}
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', mb: '10px' }}>
                  {openStories > 0 ? `${openStories} stories still open` : 'All stories done'}
                </Typography>
                {/* Progress bar */}
                <Box sx={{ height: 8, width: '100%', bgcolor: '#D4EDE5', borderRadius: 9999, overflow: 'hidden', mb: '6px' }}>
                  <Box sx={{ height: '100%', borderRadius: 9999, bgcolor: barColor(pct), width: `${Math.min(pct, 100)}%` }} />
                </Box>
                {/* Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Weight: 30%</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: valColor }}>{pct}% complete</Typography>
                </Box>
              </Box>
            );
          })()}

          {/* Issues column */}
          {(() => {
            const pct = issuesPct;
            const complete = pct >= 80 && criticalOpen === 0;
            const valColor = complete ? TEAL : '#E24B4A';
            return (
              <Box sx={{ px: '20px', py: '12px', bgcolor: '#F8FAF9', borderRadius: '8px' }}>
                {/* Icon + label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '4px' }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 600,
                    bgcolor: complete ? TEAL : '#E24B4A', color: '#FFFFFF',
                  }}>
                    {complete ? '✓' : '✕'}
                  </Box>
                  <Typography sx={{ fontSize: '0.938rem', fontWeight: 700, color: '#111827' }}>Issues resolved</Typography>
                </Box>
                {/* Fraction */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: '2px' }}>
                  <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: valColor }}>{resolvedIssues}</Typography>
                  <Typography sx={{ fontSize: '1rem', color: '#6B7280', fontWeight: 500 }}>/ {totalIssues} total</Typography>
                </Box>
                {/* Sub-label */}
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', mb: '10px' }}>
                  {criticalOpen > 0 ? `${criticalOpen} critical open` : openIssues > 0 ? `${openIssues} open` : '0 critical open \u00b7 all scopes stable'}
                </Typography>
                {/* Progress bar */}
                <Box sx={{ height: 8, width: '100%', bgcolor: '#D4EDE5', borderRadius: 9999, overflow: 'hidden', mb: '6px' }}>
                  <Box sx={{ height: '100%', borderRadius: 9999, bgcolor: barColor(pct), width: `${Math.min(pct, 100)}%` }} />
                </Box>
                {/* Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>Weight: 30%</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: valColor }}>{pct}% complete</Typography>
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>

      {/* ── Scope Readiness Tiles ── */}
      <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', p: '16px' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: '12px', borderBottom: '1px solid #E5E7EB' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ width: 3, height: 20, borderRadius: '2px', bgcolor: TEAL }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Scope readiness</Typography>
            <Box component="span" sx={{ fontSize: '13px', fontWeight: 500, bgcolor: '#ECFDF5', color: '#065F46', px: '8px', py: '2px', borderRadius: 9999, ml: '4px' }}>
              {scopeSummary.length} scopes
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.875rem', color: '#111827', fontWeight: 600 }}>Per feature branch breakdown</Typography>
        </Box>

        {/* Tile grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: '12px', mt: '12px' }}>
          {scopeSummary.map((scope) => {
            const pill = SCOPE_STATUS_PILL[scope.status] || SCOPE_STATUS_PILL.planning;
            const label = SCOPE_STATUS_LABELS[scope.status] || scope.status || 'Planning';
            const hasIssues = scope.issue_count > 0;
            const rPct = hasIssues ? scope.progress_pct : 0;
            const rColor = rPct >= 80 ? TEAL : rPct >= 50 ? '#D97706' : '#E24B4A';
            const sev = scope.severity_breakdown || {};
            const sevTotal = (sev.critical || 0) + (sev.major || 0) + (sev.minor || 0) + (sev.trivial || 0);

            // Issue status summary text + colour
            let issueStatusText = '';
            let issueStatusColor = '#9CA3AF';
            if (hasIssues) {
              if (scope.open_issues === 0) {
                issueStatusText = `${scope.issue_count} total \u00b7 ${scope.resolved_issues} resolved \u00b7 0 open`;
                issueStatusColor = TEAL;
              } else if ((sev.critical || 0) > 0) {
                issueStatusText = `${sev.critical} critical open`;
                issueStatusColor = '#EF4444';
              } else {
                issueStatusText = `${scope.open_issues} open`;
                issueStatusColor = '#D97706';
              }
            }

            return (
              <Box
                key={scope.scope_id ?? 'unscoped'}
                onClick={() => onNavigateToTab && onNavigateToTab('scopes')}
                sx={{
                  border: '1px solid #E5E7EB', borderRadius: '8px', p: '12px', cursor: 'pointer',
                  borderTop: `3px solid ${(SCOPE_STATUS_PILL[scope.status] || SCOPE_STATUS_PILL.planning).color}`,
                  bgcolor: '#FFFFFF',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                  transition: 'all 150ms',
                }}
              >
                {/* Row 1: name + status pill */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '6px' }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1F2937', lineHeight: 1.3, pr: 1 }}>
                    {scope.scope_name}
                  </Typography>
                  <Box component="span" sx={{
                    fontSize: '13px', fontWeight: 500, px: '8px', py: '3px', borderRadius: 9999, whiteSpace: 'nowrap',
                    bgcolor: pill.bg, color: pill.color,
                  }}>
                    {label}
                  </Box>
                </Box>

                {/* Row 2: branch + readiness % */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '10px' }}>
                  {scope.branch_name ? (
                    <Box component="span" sx={{
                      fontFamily: '"SF Mono",SFMono-Regular,ui-monospace,monospace',
                      fontSize: '12px', bgcolor: '#F9FAFB', px: '6px', py: '2px', borderRadius: '4px',
                      borderLeft: `2px solid ${TEAL}`, color: '#9CA3AF',
                    }}>
                      {scope.branch_name}
                    </Box>
                  ) : <Box />}
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: rColor }}>
                    {rPct}% ready
                  </Typography>
                </Box>

                {/* Stories bar */}
                {scope.total_stories > 0 && (
                  <Box sx={{ mb: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '4px' }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Stories</Typography>
                      <Typography sx={{ fontSize: '13px', color: '#374151' }}>
                        {scope.total_stories} total &middot; {scope.done_stories} done &middot; {scope.remaining_stories} left
                      </Typography>
                    </Box>
                    <Box sx={{ height: 6, width: '100%', bgcolor: '#E5E7EB', borderRadius: 9999, overflow: 'hidden' }}>
                      {scope.done_stories > 0 && (
                        <Box sx={{ height: '100%', borderRadius: 9999, bgcolor: TEAL, width: `${(scope.done_stories / scope.total_stories) * 100}%` }} />
                      )}
                    </Box>
                  </Box>
                )}

                {/* Issues bar */}
                {hasIssues && (
                  <Box sx={{ mb: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '4px' }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Issues</Typography>
                      <Typography sx={{ fontSize: '13px', color: issueStatusColor }}>{issueStatusText}</Typography>
                    </Box>
                    {/* Severity segmented bar */}
                    <Box sx={{ display: 'flex', height: 6, borderRadius: 9999, overflow: 'hidden', bgcolor: '#E5E7EB', width: '100%' }}>
                      {sevTotal > 0 && (
                        <>
                          {(sev.critical || 0) > 0 && <Box sx={{ height: '100%', width: `${(sev.critical / sevTotal) * 100}%`, bgcolor: SEV_COLORS.critical }} />}
                          {(sev.major || 0) > 0 && <Box sx={{ height: '100%', width: `${(sev.major / sevTotal) * 100}%`, bgcolor: SEV_COLORS.major }} />}
                          {(sev.minor || 0) > 0 && <Box sx={{ height: '100%', width: `${(sev.minor / sevTotal) * 100}%`, bgcolor: SEV_COLORS.minor }} />}
                          {(sev.trivial || 0) > 0 && <Box sx={{ height: '100%', width: `${(sev.trivial / sevTotal) * 100}%`, bgcolor: SEV_COLORS.trivial }} />}
                        </>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Severity badges */}
                {sevTotal > 0 && (
                  <Box sx={{ display: 'flex', gap: '8px', mt: '6px', flexWrap: 'wrap' }}>
                    {(sev.critical || 0) > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: SEV_COLORS.critical }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: SEV_COLORS.critical }}>{sev.critical} Critical</Typography>
                      </Box>
                    )}
                    {(sev.major || 0) > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEV_COLORS.major }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: SEV_COLORS.major }}>{sev.major} Major</Typography>
                      </Box>
                    )}
                    {(sev.minor || 0) > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEV_COLORS.minor }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: SEV_COLORS.minor }}>{sev.minor} Minor</Typography>
                      </Box>
                    )}
                    {(sev.trivial || 0) > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEV_COLORS.trivial }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#6B7280' }}>{sev.trivial} Trivial</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}

          {/* Add scope placeholder tile */}
          <Box
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              border: '2px dashed #D4EDE5', borderRadius: '8px', minHeight: 140,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', bgcolor: '#FFFFFF',
              '&:hover': { bgcolor: '#F8FAF9', borderColor: TEAL },
              transition: 'all 150ms',
            }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '6px' }}>
              <Typography sx={{ fontSize: '1.125rem', color: '#FFFFFF', lineHeight: 1, fontWeight: 600 }}>+</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: 500 }}>Add scope</Typography>
          </Box>
        </Box>
      </Box>

      {/* Create Scope Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Scope</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Scope Name"
            fullWidth
            required
            value={newScope.name}
            onChange={(e) => setNewScope({ ...newScope, name: e.target.value })}
            placeholder="e.g., Payment Gateway Revamp"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Branch Name"
            fullWidth
            value={newScope.branch_name}
            onChange={(e) => setNewScope({ ...newScope, branch_name: e.target.value })}
            placeholder="e.g., feat/payment-revamp"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <BranchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Scope Type</InputLabel>
            <Select
              value={newScope.scope_type}
              label="Scope Type"
              onChange={(e) => setNewScope({ ...newScope, scope_type: e.target.value })}
            >
              <MenuItem value="feature">Feature</MenuItem>
              <MenuItem value="bugfix">Bug Fix</MenuItem>
              <MenuItem value="hotfix">Hotfix</MenuItem>
              <MenuItem value="epic">Epic</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={newScope.description}
            onChange={(e) => setNewScope({ ...newScope, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateScope}
            variant="contained"
            disabled={!newScope.name.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : null}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardView;
